import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { optimizeImage, buildSrcSet } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/17092455/pexels-photo-17092455.png";
const HERO_SRC = optimizeImage(HERO_IMG, 1600);
const HERO_SRCSET = buildSrcSet(HERO_IMG, [480, 768, 1200, 1600, 1920]);

const lines = ["WHERE", "MACHINES", "BECOME"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.12 },
  }),
};

export default function Hero({ onReserve }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] w-full overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform">
        <img
          src={HERO_SRC}
          srcSet={HERO_SRCSET}
          sizes="100vw"
          alt="Supercar under studio lighting"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-black/40" />

      {/* Full-height content column: meta pinned top, headline pinned bottom (never overlap) */}
      <div className="absolute inset-0 z-10 flex flex-col justify-between px-6 pb-14 pt-28 md:px-10 md:pb-20">
        {/* Top meta row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="mx-auto flex w-full max-w-[1600px] justify-between"
        >
          <span className="font-body text-xs uppercase tracking-[0.3em] text-white/50">Est. 2024 — Cars &amp; Coffee</span>
          <span className="hidden font-body text-xs uppercase tracking-[0.3em] text-white/50 md:block">Saturday Mornings · Coffee &amp; Cars</span>
        </motion.div>

        {/* Headline block */}
        <motion.div style={{ y: textY }} className="mx-auto w-full max-w-[1600px]">
          <div className="font-display text-[14vw] font-extrabold leading-[0.85] tracking-tighter text-white md:text-[12vw] lg:text-[9vw]">
            {lines.map((line, i) => (
              <div key={line} className="overflow-hidden">
                <motion.div custom={i} variants={lineVariants} initial="hidden" animate="visible">
                  {line === "BECOME" ? (
                    <span>
                      BECOME <span className="font-serif-italic font-medium italic text-primary">art</span>
                    </span>
                  ) : (
                    line
                  )}
                </motion.div>
              </div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 flex flex-col items-start justify-between gap-6 md:mt-8 md:flex-row md:items-end md:gap-8"
          >
            <p className="max-w-md font-body text-base text-white/70 md:text-lg">
              Speed Social Events is a laid-back cars and coffee meetup. Grab a
              coffee, roll in early, and check out the best rides in town — every
              Saturday morning.
            </p>
            <button
              data-testid="hero-reserve-button"
              onClick={onReserve}
              className="group flex items-center gap-4 bg-primary px-8 py-4 transition-colors duration-300 hover:bg-white"
            >
              <span className="font-body text-sm font-medium uppercase tracking-widest text-white transition-colors duration-300 group-hover:text-background">
                Reserve Your Spot
              </span>
              <ArrowDownRight className="h-5 w-5 text-white transition-all duration-300 group-hover:rotate-45 group-hover:text-background" />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
